import { List } from 'immutable';
import { useI18n } from 'app/components/localization';
import { useMemo } from 'react';
import { formatArray } from './array';
import { defaultMemoize } from 'reselect';
import { isShallowEqual } from 'app/utils/object';

export enum ListPatternType {
  // Joins list items with "or" or equivalent word or symbol in other languages
  Or = 'or',
  // Joins list items with "and" or equivalent word or symbol in other languages
  Standard = 'standard',
  // Joins list items with just "," or equivalent word or symbol in other languages
  Unit = 'unit',
}

/**
 * Returns a function that is able to format a list of strings in a grammatically correct way.
 * It automatically provides all the format patterns to the underlying `formatArray` function.
 */
const useFormatList = () => {
  const { t } = useI18n();

  /**
   * Formats an array or list of strings in a grammatically correct way.
   * @param list
   * @param type list pattern type, check {@link ListPatternType} for usage
   */
  const formatList = useMemo(
    () =>
      defaultMemoize((list: List<string> | string[], type: ListPatternType = ListPatternType.Standard) => {
        const arr = Array.isArray(list) ? list : list.toArray();

        const twoPattern =
          type === ListPatternType.Unit
            ? t('list-patterns.unit.2')
            : type === ListPatternType.Or
            ? t('list-patterns.or.2')
            : t('list-patterns.standard.2');
        const startPattern =
          type === ListPatternType.Unit
            ? t('list-patterns.unit.start')
            : type === ListPatternType.Or
            ? t('list-patterns.or.start')
            : t('list-patterns.standard.start');
        const middlePattern =
          type === ListPatternType.Unit
            ? t('list-patterns.unit.middle')
            : type === ListPatternType.Or
            ? t('list-patterns.or.middle')
            : t('list-patterns.standard.middle');
        const endPattern =
          type === ListPatternType.Unit
            ? t('list-patterns.unit.end')
            : type === ListPatternType.Or
            ? t('list-patterns.or.end')
            : t('list-patterns.standard.end');

        return formatArray(arr, { two: twoPattern, start: startPattern, middle: middlePattern, end: endPattern });
      }, isShallowEqual),
    [t],
  );

  return formatList;
};

export default useFormatList;
